// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2017
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import Mousetrap from 'mousetrap';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import TimeSignatureSlider from './timeSignatureSlider';
import TimeSignature from '../uTheory.Notation/timeSignature';
import Music from '../uTheory.Notation/music';
import * as Skills from '../uTheory.Skills/skills';
import utils from '../utils';
import config from '../config';
import TablaInstrument from '../uTheory.Notation/tablaInstrument';
import MusicalClock from '../uTheory.Notation/musicalClock';
import MeterIdQuestion from './meterIDQuestion';
import type { RhythmQuestionObject } from './rhythmQuestionProcessor';
import Pointer from '../uTheory.Notation/pointer';
import type { Voice } from '../notation';

interface Options extends InteractiveOptions {
  /** include at least a | at the end of each excerpt */
  closeLastMeasureWithFinalBarline?: boolean;
  /** include a || at the end of each excerpt */
  closeLastMeasureWithBarline?: boolean;
}

/**
 * A game of visual meter identification -- see a meter, say what it is.
 */
class MeterID extends InteractivePrototype {
  musicalClock!: MusicalClock;

  sliders!: TimeSignatureSlider;

  slidersDiv!: HTMLDivElement;

  declare questionData: RhythmQuestionObject[];

  declare questionPrompt: string;

  declare options: Options;

  declare questions: MeterIdQuestion[];

  declare currentQuestion: MeterIdQuestion;

  /** Whether user numbers typed on keyboard are registering on the upper or lower part of time signature */
  private typingIsLowerFocused: boolean;

  // private isContinuingTyping: boolean;
  private isContinuingTyping: boolean = false;

  constructor(displayDiv: HTMLDivElement, options: Options = {}) {
    options = {
      closeLastMeasureWithFinalBarline: true,
      closeLastMeasureWithBarline: true,
      ...options,
    };

    super(displayDiv, options);

    // Place custom settings & Layout information here:
    this.settings = {
      instructions: true,
      notation: {
        ...options,
        ariaTitle: {
          letterName: false,
          octave: false,
          keySignature: false,
          accidental: false,
        },
      },
      letters: {
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      uPrefs: options.testMode ? false : { appName: 'rhythmReading', optional: false },
      questionData: options.drill && {
        filename: 'measure-rhythms',
        directory: `${config.apiUrl}/${config.questionsDirectory}`,
      },
    };

    // console.log('options.drill?', options.drill);
    // console.log('questionData?', this.settings.questionData);
    // get the right kinds of questions in questiondata:
    if (options.drill && this.settings.questionData) {
      if (utils.typeof(options.skills) !== 'array') {
        throw new Error('options.skills array must be given in drill mode.');
      }
      const skills = options.skills!.map(Skills.makeString);
      const includeSimple = skills.includes('meter_id_simple');
      const includeCompound = skills.includes('meter_id_compound');

      // console.log(`*********${options.skills}**********`);
      // console.log(`*********${skills}**********`);
      this.settings.questionData.filename += utils.uriEncode({
        includeSimple,
        includeCompound,
        includeCutTime: false,
        appName: 'meterId',
      });
    }

    this.layout = {
      instructions: { percent: 15, noWrap: true, style: { marginBottom: '0.1em' } },
      notation: { percent: 45 },
      sliders: {
        name: 'timeSignatureSliders',
        percent: 40,
        visible: true,
      },
      letters: { percent: 10 },
    };
    this.isContinuingTyping = false;
    this.typingIsLowerFocused = false;
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: Options): Promise<any> {
    this.musicalClock = new MusicalClock();
    return super.init(displayDiv, options);
  }

  createCustomDivs(): void {
    const slidersDiv = this.createDiv('slidersDiv', {
      textAlign: 'left',
      margin: 'none',
      width: '',
    });

    this.layout.sliders!.div = slidersDiv;
    this.displayDiv.insertBefore(slidersDiv, this.lettersDiv!);

    this.music = Music.fromString(`meter:4/4 key:C/Major
clef:percussion B4/w |`);

    const dummyTimeSig = new TimeSignature('4/4');
    this.sliders = new TimeSignatureSlider(slidersDiv, dummyTimeSig, {
      onChange: (val: any) => {
        this.onChange(val);
      },
    });

    this.layout.sliders!.callback = this.sliders.resize.bind(this.sliders);
    this.layout.sliders!.visible = true;
  }

  generateQuestions(
    questions: RhythmQuestionObject[] | string[] = this.options.questions || this.questionData
  ): void {
    const maxQuestions = this.options.drill ? this.options.maxQuestions || 10 : questions.length;

    function newQuestion(q: RhythmQuestionObject | string, i: number) {
      return new MeterIdQuestion(q, i);
    }

    this.questions = this.options.drill
      ? this.questionData.slice(-maxQuestions).map(newQuestion)
      : (questions as string[]).map(newQuestion);

    this.questionPrompt = 'What meter is this music in?<br /><small>Move the sliders.</small>';
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>{question} of ${this.questions.length}</small`;
    }
  }

  onChange(value: string): void {
    this.currentQuestion.timeSignature.displayAs = value;
    this.notation!.render();
  }

  addListeners(): void {
    this.letterPanel!.addCheckCallback(() => {
      this.checkAnswer();
    }, this);
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, '/', 'del', 'backspace'].forEach((key) => {
      Mousetrap.bind(`${key}`, () => this.typeAnswer(`${key}`));
    });
  }

  /** Called when user types an answer on the keyboard */
  typeAnswer(key: string): void {
    if (this.sliders.disabled) return;
    if (key === '/') {
      this.typingIsLowerFocused = !this.typingIsLowerFocused;
      this.isContinuingTyping = false;
      return;
    }

    const currentSliderValue = `${this.sliders[this.typingIsLowerFocused ? 'lower' : 'upper']}`;

    let newVal: string;
    if (key === 'del' || key === 'backspace') {
      if (currentSliderValue.length === 1) {
        newVal = '1';
        this.isContinuingTyping = false;
        console.log('reset to 1');
      } else {
        newVal = currentSliderValue.slice(0, -1);
        console.log(`reset to ${newVal}, continuing typing.`);
      }
    } else {
      // it's a number
      newVal = `${this.isContinuingTyping ? currentSliderValue : ''}${key}`;
      const maxVal = this.typingIsLowerFocused ? 32 : 15;
      console.log({ newVal, maxVal });
      if (Number(newVal) > maxVal) {
        newVal = key;
      } else {
        this.isContinuingTyping = true;
      }
    }

    console.log({ newVal, isContinuingTyping: this.isContinuingTyping, final: true });

    if (this.typingIsLowerFocused) {
      this.sliders.lower = Number(newVal);
    } else {
      this.sliders.upper = Number(newVal);
    }

    this.currentQuestion.timeSignature.displayAs = `${this.sliders.upper}/${this.sliders.lower}`;
    this.notation!.render();
  }

  newQuestion(): void {
    this.sliders.disabled = false;
    this.typingIsLowerFocused = false;
    super.newQuestion();
    this.music = this.currentQuestion.music;
    this.notation!.music = this.music;
    // this.sliders.timeSignature = this.currentQuestion.timeSignature;
    this.sliders.set('1/1');
    this.isContinuingTyping = false;
  }

  newCheckpointQuestion(questionString: string): Promise<MeterIdQuestion> {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  checkAnswer(): void {
    const correct = this.currentQuestion.check();
    if (this.options.testMode) return this.checkpointAnswer(correct);
    if (this.rightAnswerDelay) return undefined;

    if (correct) {
      this.questionPrompt = this.questionPrompt.replace(
        '<br /><small>Move the sliders.</small>',
        ''
      );
      this.currentQuestion.timeSignature.set(
        this.currentQuestion.timeSignature.displayAs as TimeSignatureAlias
      );
      if (this.uPrefs) {
        const question = this.currentQuestion;
        this.uPrefs.saveQuestion(question.id, 1 / (this.wrongAnswerCount + 1)).catch((err) => {
          console.error(err);
        });
      }
      this.sliders.disabled = true;
      // 0.5s pause before playing, playback time, and 0.5s pause after.
      return this.rightAnswer(this.currentQuestion.stopTime * 1000 + 1000);
    }
    this.wrongAnswerCount++;
    return this.wrongAnswer(this.hint());
  }

  hint(): string | undefined {
    if (this.wrongAnswerCount <= 1) return undefined;
    let wrongTS;
    const rightTS = this.currentQuestion.timeSignature;
    const [upper, lower] = rightTS.displayAs.split('/').map(Number);
    try {
      wrongTS = new TimeSignature(
        this.currentQuestion.timeSignature.displayAs as TimeSignatureAlias
      );
    } catch (err) {
      wrongTS = null;
    }

    if (!wrongTS) {
      if ([1, 2, 4, 8, 16, 32, 64, 128].includes(lower as number)) {
        if (lower !== rightTS.lower) {
          return (lower as number) > rightTS.lower
            ? 'Try a longer beat duration'
            : 'Try a shorter beat duration.';
        }
        if (upper !== rightTS.upper) {
          return (upper as number) > rightTS.upper ? 'Too many beats' : 'Too few beats';
        }
      } else return "That time signature doesn't exist";
    }

    if (rightTS.isCompound && (!wrongTS || !wrongTS.isCompound)) return 'Try a compound meter.';
    if (rightTS.isSimple && (!wrongTS || !wrongTS.isSimple)) return 'Try a simple meter.';
    if (wrongTS && !wrongTS.beatDuration.equals(rightTS.beatDuration)) {
      const rightFract = rightTS.beatDuration.getFraction();
      const wrongFract = wrongTS.beatDuration.getFraction();
      return rightFract.greaterThan(wrongFract)
        ? 'Try a longer beat duration.'
        : 'Try a shorter beat duration.';
    }
    return wrongTS && wrongTS.beatsPerMeasure > rightTS.beatsPerMeasure
      ? 'Too many beats.'
      : 'Too few beats.';
  }

  playAnswer(): void {
    const mc: MusicalClock = this.musicalClock;
    const voice = this.music!.voices[0] as Voice;
    if (this.music!.staves[0]!.clef.equals('percussion')) {
      voice.setInstrument(new TablaInstrument());
    }
    mc.setMeter(this.currentQuestion.timeSignature);
    mc.setTempo(this.currentQuestion.tempo);
    mc.enableMetronome();
    mc.startMetronome();
    mc.queue({
      startPointer: new Pointer('0:0:0', this.currentQuestion.timeSignature),
      sound: voice,
    });
    mc.stopMetronome({ startPointer: '1:0:0' });
    mc.stop({ stopPointer: this.currentQuestion.stopPointer });
    utils.wait(0.5).then(() => mc.play());
  }

  gameOver(): void {
    super.gameOver();
    this.displayDiv.removeChild(this.slidersDiv);
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(): AnswerAndData {
    return this.currentQuestion.getAnswerAndData(this);
  }

  static registry: Record<string, any> = {
    description: 'What meter is this music in?',
    skills: ['meter_id_compound', 'meter_id_simple'],
  };
}
export default MeterID;
